import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { DashboardWidgetChartComponent } from '../../dashboard-widget-chart/dashboard-widget-chart.component';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { CommunicationService } from 'src/app/services/communication';
import { ParameterHandlerService, CacheService } from 'projects/common/src/app/services';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import Highcharts from 'highcharts';
import { DrillsMachineService } from '../../../services/drills/drills-machine.service';
import { IUser } from 'projects/common/src/app/interfaces';
import { IShift } from 'src/app/modules/core/interfaces';
import { IDrillsWidget } from 'src/app/modules/widget/interfaces/drills-widget';
import { IMachineContext } from 'projects/common/src/app/interfaces/machine-context';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';

@Component({
  selector: 'app-drills-by-machine',
  templateUrl: './drills-by-machine.component.html',
  styleUrls: ['./drills-by-machine.component.scss'],
})
export class DrillsByMachineComponent extends DashboardWidgetChartComponent implements OnInit, OnDestroy {
  public hasData: boolean;

  constructor(
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected sidebarService: StateSidebarService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    private drillsMachineService: DrillsMachineService,
    protected screenViewService: ScreenViewService,
    public element: ElementRef,
    public languageService: LanguageService,
    public datePipe: DatePresentationPipe,
  ) {
    super(progressBarService, communication, parameterService, sidebarService, cacheService, machineContext, screenViewService);
  }

  /**
   * Function to draw the chart view
   *
   * @param chartOptions Chart options
   */
  public drawView(chartOptions: any): void {
    this.drillsMachineService
      .getDrillsByMachine()
      .toPromise()
      .then((response: any) => {
        const drillsData: IDrillsWidget[] = response.data as IDrillsWidget[];

        const series = this.generateSeries(drillsData);
        this.hasData = !!series.length;
        const drillsMeasure: { manual: number; automatic: number } = this.calculateMeasures(drillsData);

        // Get the current application parameter
        const currentMachine = this.parameterService.parameters.item('machine-context').value || 'no disponible';

        // TODO: Mejorar la obtención de tipo de máquina
        const machineCollection = this.parameterService.parameters.item('machine-collection').value as IMachineContext[];
        const machineType = machineCollection.find(
          (value) => String(value.name).toLowerCase() === String(currentMachine.name).toLowerCase(),
        );

        const date = this.parameterService.parameters.item('date').value;
        const shift = this.parameterService.parameters.item('shift').value as IShift;
        const user = this.parameterService.parameters.item('user-data').value as IUser;

        // Get machine names
        const allMachineNames = drillsData.map((value) => value.machineName);
        const machineNames = allMachineNames.filter((v, i) => allMachineNames.indexOf(v) === i);
        const data = {
          chart: {
            type: 'bar',
          },
          credits: {
            enabled: false,
          },
          title: {
            text: '',
          },
          subtitle: {
            text: `${this.languageService.message('company')}: ${user.company.name},
                  ${this.languageService.message('device')}: ${this.languageService.message(machineType.name)},
                  ${this.languageService.message('date')}: ${this.datePipe.transform(date)},
                  ${this.languageService.message('shift_')}: ${shift.name}`,
          },
          xAxis: {
            title: {
              text: 'EQUIPOS',
            },
            categories: machineNames,
          },
          yAxis: {
            min: 0,
            title: {
              // TODO: Traducir
              text: 'METROS',
            },
          },
          legend: {
            reversed: true,
          },
          plotOptions: {
            series: {
              stacking: 'normal',
            },
          },
          series,
        };

        this.chart = Highcharts.chart(this.chartTarget.nativeElement, data as any, (chart) => {
          let detailX = chart.plotWidth / 2 - 50;
          const detailY = 35;

          // Create the detail of drills
          if (drillsMeasure.automatic > 0 && drillsMeasure.manual > 0) {
            detailX = 350;
          } else if (drillsMeasure.automatic > 0 && drillsMeasure.manual < 1) {
            detailX -= 300;
          }

          if (drillsMeasure.manual) {
            chart.renderer
              .symbol('square', detailX - 50, detailY, 50, 20)
              .attr({ fill: '#29BCDE' })
              .add();
            chart.renderer
              .text(`Metros manuales: ${drillsMeasure.manual.toFixed(2)}`, detailX + 10, detailY + 14)
              .css({ fontSize: '12px' })
              .add();
          }

          if (drillsMeasure.automatic) {
            chart.renderer
              .symbol('square', detailX + 250, detailY, 50, 20)
              .attr({ fill: '#F7D76D' })
              .add();
            chart.renderer
              .text(`Metros automáticos: ${drillsMeasure.automatic.toFixed(2)}`, detailX + 310, detailY + 14)
              .css({ fontSize: '12px' })
              .add();
          }

          // If doesn't exists data, put a text
          if (!data.series || !data.series.length) {
            const newX = chart.plotWidth / 3 + chart.plotLeft;
            const newY = chart.plotHeight / 2 + chart.plotTop;
            chart.renderer
              .text('La solicitud no arrojó resultados', newX, newY)
              .css({
                color: '#4572A7',
                fontSize: '24px',
              })
              .add();
          }
        });
      });
  }

  /**
   * Function to generate a highchart serie by machines
   *
   * @param machines Machines to group and convert to highchart serie
   */
  private generateSeries(machines: IDrillsWidget[]): any {
    return this.drillsMachineService.groupDrillByMachine(machines);
  }

  /**
   * Function to calculate manual and automatic drills
   */
  private calculateMeasures(drillsData: IDrillsWidget[]): { automatic: number; manual: number } {
    const manualDrills = drillsData.filter((value) => value.drillOperationTypeId !== '2');
    const automaticDrills = drillsData.filter((value) => value.drillOperationTypeId === '2');

    const manualMapped = manualDrills ? manualDrills.map((value) => value.drillDepth) : [];
    const automaticMapped = automaticDrills ? automaticDrills.map((value) => value.drillDepth) : [];

    const manualDepth = manualMapped.length > 0 ? manualMapped.reduce((a, b) => a + b) : 0;
    const automaticDepth = automaticMapped.length > 0 ? automaticMapped.reduce((a, b) => a + b) : 0;

    return {
      automatic: automaticDepth,
      manual: manualDepth,
    };
  }

  /**
   * On destroy component
   */
  public onDestroy(): void {
    super.ngOnDestroy();
  }
}
